import React, { useState } from 'react';

const WrenchIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const LightningIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
  </svg>
);

const ToolsModePanel = ({ loading, setStatus }) => {
  const [activeTool, setActiveTool] = useState('decimals');
  const [decimalPlaces, setDecimalPlaces] = useState(3);
  const [padLength, setPadLength] = useState(5);
  const [padChar, setPadChar] = useState('0');
  const [caseType, setCaseType] = useState('upper');
  const [prefixText, setPrefixText] = useState('');
  const [suffixText, setSuffixText] = useState('');
  const [processedCount, setProcessedCount] = useState(0);
  const [dateFormat, setDateFormat] = useState('MM-YYYY'); // Format for date fix
  const [serialDateFormat, setSerialDateFormat] = useState('MM/DD/YYYY'); // Format for serial to date
  const [dateFixSubTool, setDateFixSubTool] = useState('reverse'); // Sub-tool within Fix Date Conv.
  const [isoOutputFormat, setIsoOutputFormat] = useState('MM/DD/YYYY'); // Format for ISO timestamp parse

  const applyToSelection = async (transformFn, forceTextFormat = true) => {
    try {
      let count = 0;
      await Excel.run(async (context) => {
        const range = context.workbook.getSelectedRange();
        range.load("values, rowCount, columnCount");
        await context.sync();

        const rows = range.rowCount;
        const cols = range.columnCount;

        const newValues = range.values.map(row => 
          row.map(cell => {
            if (cell === null || cell === undefined || cell === "") return cell;
            const transformed = transformFn(cell);
            if (String(transformed) !== String(cell)) count++;
            return transformed;
          })
        );

        // CRITICAL: Set format to TEXT first so Excel doesn't convert values
        // This preserves leading zeros (09390) and exact decimals (2.200)
        if (forceTextFormat) {
          const textFormat = Array(rows).fill(null).map(() => Array(cols).fill("@"));
          range.numberFormat = textFormat;
          await context.sync();
        }

        range.values = newValues;
        await context.sync();
      });
      setProcessedCount(count);
      setStatus(`Success! Transformed ${count} cell(s).`);
    } catch (err) {
      setStatus(`Error: ${err.message}`);
    }
  };

  const handleDecimalFormat = () => {
    applyToSelection((cell) => {
      const num = parseFloat(cell);
      if (isNaN(num)) return cell;
      return num.toFixed(decimalPlaces);
    });
  };

  const handlePadLeading = () => {
    applyToSelection((cell) => {
      const str = String(cell).trim();
      if (str.length >= padLength) return str;
      return str.padStart(padLength, padChar);
    });
  };

  const handlePadTrailing = () => {
    applyToSelection((cell) => {
      const str = String(cell).trim();
      if (str.length >= padLength) return str;
      return str.padEnd(padLength, padChar);
    });
  };

  const handleTrim = () => {
    applyToSelection((cell) => {
      if (typeof cell === 'string') {
        return cell.trim().replace(/\s+/g, ' ');
      }
      return cell;
    });
  };

  const handleCase = () => {
    applyToSelection((cell) => {
      if (typeof cell !== 'string') return cell;
      switch (caseType) {
        case 'upper': return cell.toUpperCase();
        case 'lower': return cell.toLowerCase();
        case 'title': return cell.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
        case 'sentence': return cell.charAt(0).toUpperCase() + cell.slice(1).toLowerCase();
        default: return cell;
      }
    });
  };

  const handlePrefixSuffix = () => {
    applyToSelection((cell) => {
      if (cell === null || cell === undefined || cell === "") return cell;
      return `${prefixText}${cell}${suffixText}`;
    });
  };

  // Protect text from Excel auto-conversion by prefixing with apostrophe
  const handleProtectText = () => {
    applyToSelection((cell) => {
      if (cell === null || cell === undefined || cell === "") return cell;
      const str = String(cell);
      // Don't double-protect
      if (str.startsWith("'")) return str;
      return "'" + str;
    });
  };

  // Fix Excel date conversion - extracts month/year from Excel date serial
  const handleFixDateConversion = () => {
    applyToSelection((cell) => {
      if (cell === null || cell === undefined || cell === "") return cell;
      
      let date = null;
      
      // Check if it's an Excel date serial number (numeric value)
      if (typeof cell === 'number') {
        // Excel date serial: days since Dec 30, 1899
        // Excel incorrectly considers 1900 a leap year, so dates after Feb 28, 1900 are off by 1
        const excelEpoch = new Date(Date.UTC(1899, 11, 30)); // Dec 30, 1899
        const msPerDay = 24 * 60 * 60 * 1000;
        date = new Date(excelEpoch.getTime() + cell * msPerDay);
      }
      // Check if it's already a Date object (shouldn't happen from Excel, but just in case)
      else if (cell instanceof Date) {
        date = cell;
      }
      // Check if it looks like a date string Excel created
      else if (typeof cell === 'string') {
        // Try parsing common Excel date formats like "1/1/5320" or "Jan-20"
        const slashMatch = cell.match(/^(\d{1,2})\/(\d{1,2})\/(\d+)$/);
        if (slashMatch) {
          const month = parseInt(slashMatch[1], 10);
          const year = parseInt(slashMatch[3], 10);
          // Reconstruct based on format
          if (dateFormat === 'MM-YYYY') {
            return String(month).padStart(2, '0') + '-' + year;
          } else if (dateFormat === 'YYYY-MM') {
            return year + '-' + String(month).padStart(2, '0');
          } else if (dateFormat === 'MM/YYYY') {
            return String(month).padStart(2, '0') + '/' + year;
          }
        }
        
        // Try parsing "Jan-20" format (abbreviated month-year)
        const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
        const abbrevMatch = cell.toLowerCase().match(/^([a-z]{3})-(\d{2,4})$/);
        if (abbrevMatch) {
          const monthIdx = monthNames.indexOf(abbrevMatch[1]);
          if (monthIdx !== -1) {
            const month = monthIdx + 1;
            let year = parseInt(abbrevMatch[2], 10);
            // If 2-digit year, it's likely a truncated year from a large number
            // But we can't recover the full year from just "20", so return as-is
            // unless user provides more context
            if (abbrevMatch[2].length === 2) {
              // Can't reliably recover - the year was truncated
              // Return original or best guess
              return cell; // Can't fix without more info
            }
            if (dateFormat === 'MM-YYYY') {
              return String(month).padStart(2, '0') + '-' + year;
            }
          }
        }
        
        // Not a date we can parse, return as-is
        return cell;
      }
      
      if (date && !isNaN(date.getTime())) {
        const month = date.getUTCMonth() + 1; // 0-indexed
        const year = date.getUTCFullYear();
        
        // Build the output based on selected format
        if (dateFormat === 'MM-YYYY') {
          return String(month).padStart(2, '0') + '-' + year;
        } else if (dateFormat === 'YYYY-MM') {
          return year + '-' + String(month).padStart(2, '0');
        } else if (dateFormat === 'MM/YYYY') {
          return String(month).padStart(2, '0') + '/' + year;
        }
      }
      
      // Couldn't parse as date, return original
      return cell;
    });
  };

  // Convert Excel date serial number to date text
  const handleSerialToDate = () => {
    applyToSelection((cell) => {
      if (cell === null || cell === undefined || cell === "") return cell;
      
      // Only process numeric values (Excel date serials)
      const num = typeof cell === 'number' ? cell : parseFloat(cell);
      if (isNaN(num) || num < 1 || num > 2958465) return cell; // Valid Excel date range
      
      // Excel date serial: days since Dec 30, 1899
      const excelEpoch = new Date(Date.UTC(1899, 11, 30)); // Dec 30, 1899
      const msPerDay = 24 * 60 * 60 * 1000;
      const date = new Date(excelEpoch.getTime() + num * msPerDay);
      
      if (isNaN(date.getTime())) return cell;
      
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      const year = date.getUTCFullYear();
      
      switch (serialDateFormat) {
        case 'MM/DD/YYYY':
          return `${month}/${day}/${year}`;
        case 'DD/MM/YYYY':
          return `${day}/${month}/${year}`;
        case 'YYYY-MM-DD':
          return `${year}-${month}-${day}`;
        case 'MM-DD-YYYY':
          return `${month}-${day}-${year}`;
        default:
          return `${month}/${day}/${year}`;
      }
    });
  };

  // Parse ISO timestamp strings like "1979-03-13T00:00:00" into date text
  const handleIsoTimestamp = () => {
    applyToSelection((cell) => {
      if (cell === null || cell === undefined || cell === "") return cell;
      const str = String(cell).trim();
      
      // Match ISO 8601 patterns: YYYY-MM-DD, YYYY-MM-DDTHH:MM:SS, with optional timezone/ms
      const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}):?(\d{2})?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?)?$/);
      if (!isoMatch) return cell;
      
      const year = isoMatch[1];
      const month = isoMatch[2];
      const day = isoMatch[3];
      
      switch (isoOutputFormat) {
        case 'MM/DD/YYYY':
          return `${month}/${day}/${year}`;
        case 'DD/MM/YYYY':
          return `${day}/${month}/${year}`;
        case 'YYYY-MM-DD':
          return `${year}-${month}-${day}`;
        case 'MM-DD-YYYY':
          return `${month}-${day}-${year}`;
        default:
          return `${month}/${day}/${year}`;
      }
    });
  };

  // Convert text dates to actual Excel date serial numbers for proper sorting/calculation
  const handleTextToRealDate = async () => {
    try {
      let count = 0;
      await Excel.run(async (context) => {
        const range = context.workbook.getSelectedRange();
        range.load("values, rowCount, columnCount");
        await context.sync();

        const rows = range.rowCount;
        const cols = range.columnCount;

        const newValues = [];
        for (let r = 0; r < rows; r++) {
          const row = [];
          for (let c = 0; c < cols; c++) {
            const cell = range.values[r][c];
            if (cell === null || cell === undefined || cell === "") {
              row.push(cell);
              continue;
            }
            
            const str = String(cell).trim();
            let parsed = null;
            
            // Try MM/DD/YYYY or M/D/YYYY
            let m = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
            if (m) {
              parsed = { month: parseInt(m[1]), day: parseInt(m[2]), year: parseInt(m[3]) };
            }
            
            // Try MM-DD-YYYY or M-D-YYYY
            if (!parsed) {
              m = str.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
              if (m) {
                parsed = { month: parseInt(m[1]), day: parseInt(m[2]), year: parseInt(m[3]) };
              }
            }
            
            // Try YYYY-MM-DD
            if (!parsed) {
              m = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
              if (m) {
                parsed = { month: parseInt(m[2]), day: parseInt(m[3]), year: parseInt(m[1]) };
              }
            }
            
            // Try DD/MM/YYYY (only if day > 12 to avoid ambiguity, otherwise assume MM/DD)
            // This is intentionally conservative - we default to MM/DD/YYYY interpretation
            
            if (parsed && parsed.month >= 1 && parsed.month <= 12 && parsed.day >= 1 && parsed.day <= 31) {
              // Convert to Excel serial number
              // Excel serial: days since Dec 30, 1899 (with the 1900 leap year bug)
              const date = new Date(Date.UTC(parsed.year, parsed.month - 1, parsed.day));
              if (!isNaN(date.getTime())) {
                const excelEpoch = new Date(Date.UTC(1899, 11, 30));
                const msPerDay = 24 * 60 * 60 * 1000;
                let serial = Math.round((date.getTime() - excelEpoch.getTime()) / msPerDay);
                // Excel 1900 leap year bug: dates after Feb 28, 1900 are off by 1
                if (serial > 59) serial += 1;
                row.push(serial);
                count++;
                continue;
              }
            }
            
            row.push(cell); // Couldn't parse, leave as-is
          }
          newValues.push(row);
        }

        // Set format to date (NOT text) so Excel treats the serial numbers as dates
        const dateFormat = Array(rows).fill(null).map(() => Array(cols).fill("mm/dd/yyyy"));
        range.numberFormat = dateFormat;
        await context.sync();

        range.values = newValues;
        await context.sync();
      });
      setProcessedCount(count);
      setStatus(`Success! Converted ${count} cell(s) to real Excel dates.`);
    } catch (err) {
      setStatus(`Error: ${err.message}`);
    }
  };

  // Strip time portion from datetime strings, leaving just the date
  const handleStripTime = () => {
    applyToSelection((cell) => {
      if (cell === null || cell === undefined || cell === "") return cell;
      const str = String(cell).trim();
      
      // Match datetime with T separator or space separator
      const m = str.match(/^(\d{4}-\d{2}-\d{2})[T ]\d{2}:\d{2}/);
      if (m) return m[1];
      
      // Match MM/DD/YYYY HH:MM:SS pattern
      const m2 = str.match(/^(\d{1,2}\/\d{1,2}\/\d{4})\s+\d{1,2}:\d{2}/);
      if (m2) return m2[1];
      
      return cell;
    });
  };

  const tools = [
    { id: 'decimals', label: 'Decimal Places', icon: '🔢' },
    { id: 'pad', label: 'Pad Text', icon: '↔️' },
    { id: 'trim', label: 'Trim Spaces', icon: '✂️' },
    { id: 'case', label: 'Change Case', icon: 'Aa' },
    { id: 'prefix', label: 'Prefix/Suffix', icon: '➕' },
    { id: 'protect', label: 'Protect Text', icon: '🛡️' },
    { id: 'serialtodate', label: 'Serial → Date', icon: '📅' },
    { id: 'fixdate', label: 'Fix Date Conv.', icon: '🔄' },
  ];

  return (
    <div className="bg-gray-800 p-4 rounded shadow-sm border border-cyan-500/30 mb-6">
      <div className="flex items-center gap-2 mb-4 text-cyan-400 bg-cyan-500/10 p-2 rounded border border-cyan-500/20">
        <WrenchIcon />
        <span className="text-xs font-bold uppercase">Data Tools</span>
      </div>

      <p className="text-sm text-gray-300 mb-4">
        Select cells in Excel, choose a tool, and apply. Changes modify actual cell values, not just formatting.
      </p>

      {/* Tool Selector */}
      <div className="flex flex-wrap gap-1 mb-4">
        {tools.map(tool => (
          <button
            key={tool.id}
            onClick={() => setActiveTool(tool.id)}
            className={`px-3 py-2 rounded text-xs font-medium border transition-colors ${
              activeTool === tool.id
                ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-400'
                : 'bg-gray-700 border-gray-600 text-gray-400 hover:bg-gray-600'
            }`}
          >
            <span className="mr-1">{tool.icon}</span> {tool.label}
          </button>
        ))}
      </div>

      {/* Tool Options */}
      <div className="bg-gray-700 border border-gray-600 rounded p-4 mb-4">
        {activeTool === 'decimals' && (
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Fixed Decimal Places</label>
            <p className="text-xs text-gray-500 mb-3">Convert numbers to exact decimal places (e.g., 2.2 → 2.200)</p>
            <div className="flex items-center gap-3">
              <input
                type="number"
                min="0"
                max="10"
                value={decimalPlaces}
                onChange={(e) => setDecimalPlaces(parseInt(e.target.value) || 0)}
                className="w-20 p-2 border border-gray-600 rounded text-sm bg-gray-800 text-white"
              />
              <span className="text-xs text-gray-400">decimal places</span>
            </div>
            <div className="mt-3 text-xs text-gray-500">
              Preview: 2.2 → <span className="text-cyan-400 font-mono">{(2.2).toFixed(decimalPlaces)}</span>
            </div>
            <button
              onClick={handleDecimalFormat}
              disabled={loading}
              className="mt-4 w-full py-2 px-4 bg-cyan-500 hover:bg-cyan-400 disabled:bg-gray-600 text-gray-900 rounded font-bold text-sm flex items-center justify-center gap-2"
            >
              <LightningIcon /> Apply to Selection
            </button>
          </div>
        )}

        {activeTool === 'pad' && (
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Pad Text</label>
            <p className="text-xs text-gray-500 mb-3">Add leading or trailing characters to reach a fixed length</p>
            <div className="flex items-center gap-3 mb-3">
              <div>
                <label className="block text-[10px] text-gray-500 mb-1">Total Length</label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={padLength}
                  onChange={(e) => setPadLength(parseInt(e.target.value) || 1)}
                  className="w-20 p-2 border border-gray-600 rounded text-sm bg-gray-800 text-white"
                />
              </div>
              <div>
                <label className="block text-[10px] text-gray-500 mb-1">Pad Character</label>
                <input
                  type="text"
                  maxLength="1"
                  value={padChar}
                  onChange={(e) => setPadChar(e.target.value || '0')}
                  className="w-16 p-2 border border-gray-600 rounded text-sm bg-gray-800 text-white text-center font-mono"
                />
              </div>
            </div>
            <div className="text-xs text-gray-500 mb-3">
              Preview: 9390 → <span className="text-cyan-400 font-mono">{"9390".padStart(padLength, padChar)}</span> (leading) 
              or <span className="text-cyan-400 font-mono">{"9390".padEnd(padLength, padChar)}</span> (trailing)
            </div>
            <div className="flex gap-2">
              <button
                onClick={handlePadLeading}
                disabled={loading}
                className="flex-1 py-2 px-4 bg-cyan-500 hover:bg-cyan-400 disabled:bg-gray-600 text-gray-900 rounded font-bold text-sm"
              >
                ⚡ Pad Leading
              </button>
              <button
                onClick={handlePadTrailing}
                disabled={loading}
                className="flex-1 py-2 px-4 bg-cyan-500 hover:bg-cyan-400 disabled:bg-gray-600 text-gray-900 rounded font-bold text-sm"
              >
                ⚡ Pad Trailing
              </button>
            </div>
          </div>
        )}

        {activeTool === 'trim' && (
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Trim Whitespace</label>
            <p className="text-xs text-gray-500 mb-3">Remove leading/trailing spaces and collapse multiple spaces into one</p>
            <div className="text-xs text-gray-500 mb-3">
              Preview: "  hello   world  " → <span className="text-cyan-400 font-mono">"hello world"</span>
            </div>
            <button
              onClick={handleTrim}
              disabled={loading}
              className="w-full py-2 px-4 bg-cyan-500 hover:bg-cyan-400 disabled:bg-gray-600 text-gray-900 rounded font-bold text-sm flex items-center justify-center gap-2"
            >
              <LightningIcon /> Trim Selection
            </button>
          </div>
        )}

        {activeTool === 'case' && (
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Change Case</label>
            <p className="text-xs text-gray-500 mb-3">Convert text to different case formats</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {[
                { id: 'upper', label: 'UPPERCASE', preview: 'HELLO WORLD' },
                { id: 'lower', label: 'lowercase', preview: 'hello world' },
                { id: 'title', label: 'Title Case', preview: 'Hello World' },
                { id: 'sentence', label: 'Sentence case', preview: 'Hello world' },
              ].map(opt => (
                <button
                  key={opt.id}
                  onClick={() => setCaseType(opt.id)}
                  className={`px-3 py-2 rounded text-xs font-medium border ${
                    caseType === opt.id
                      ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400'
                      : 'bg-gray-600 border-gray-500 text-gray-300 hover:bg-gray-500'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="text-xs text-gray-500 mb-3">
              Preview: "hELLo WoRLd" → <span className="text-cyan-400 font-mono">
                {caseType === 'upper' ? 'HELLO WORLD' : caseType === 'lower' ? 'hello world' : caseType === 'title' ? 'Hello World' : 'Hello world'}
              </span>
            </div>
            <button
              onClick={handleCase}
              disabled={loading}
              className="w-full py-2 px-4 bg-cyan-500 hover:bg-cyan-400 disabled:bg-gray-600 text-gray-900 rounded font-bold text-sm flex items-center justify-center gap-2"
            >
              <LightningIcon /> Apply to Selection
            </button>
          </div>
        )}

        {activeTool === 'prefix' && (
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Add Prefix / Suffix</label>
            <p className="text-xs text-gray-500 mb-3">Add text before and/or after each cell value</p>
            <div className="flex gap-3 mb-3">
              <div className="flex-1">
                <label className="block text-[10px] text-gray-500 mb-1">Prefix (before)</label>
                <input
                  type="text"
                  value={prefixText}
                  onChange={(e) => setPrefixText(e.target.value)}
                  placeholder="e.g. ID-"
                  className="w-full p-2 border border-gray-600 rounded text-sm bg-gray-800 text-white"
                />
              </div>
              <div className="flex-1">
                <label className="block text-[10px] text-gray-500 mb-1">Suffix (after)</label>
                <input
                  type="text"
                  value={suffixText}
                  onChange={(e) => setSuffixText(e.target.value)}
                  placeholder="e.g. -2024"
                  className="w-full p-2 border border-gray-600 rounded text-sm bg-gray-800 text-white"
                />
              </div>
            </div>
            <div className="text-xs text-gray-500 mb-3">
              Preview: "12345" → <span className="text-cyan-400 font-mono">{prefixText}12345{suffixText}</span>
            </div>
            <button
              onClick={handlePrefixSuffix}
              disabled={loading || (!prefixText && !suffixText)}
              className="w-full py-2 px-4 bg-cyan-500 hover:bg-cyan-400 disabled:bg-gray-600 text-gray-900 rounded font-bold text-sm flex items-center justify-center gap-2"
            >
              <LightningIcon /> Apply to Selection
            </button>
          </div>
        )}

        {activeTool === 'protect' && (
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-2">🛡️ Protect Text from Auto-Conversion</label>
            <p className="text-xs text-gray-500 mb-3">
              Prevents Excel from converting values like <span className="font-mono text-orange-400">01-5320</span> into dates when saving as CSV.
            </p>
            <div className="bg-gray-800 border border-gray-600 rounded p-3 mb-3">
              <div className="text-xs text-gray-400 mb-2">How it works:</div>
              <ul className="text-xs text-gray-500 space-y-1">
                <li>• Adds a leading apostrophe (<span className="font-mono text-cyan-400">'</span>) to each cell</li>
                <li>• Excel treats the value as text, not a formula or date</li>
                <li>• The apostrophe is hidden in Excel but preserved in CSV</li>
              </ul>
            </div>
            <div className="text-xs text-gray-500 mb-3">
              Preview: <span className="font-mono text-orange-400">01-5320</span> → <span className="text-cyan-400 font-mono">'01-5320</span> (displays as 01-5320)
            </div>
            <button
              onClick={handleProtectText}
              disabled={loading}
              className="w-full py-2 px-4 bg-cyan-500 hover:bg-cyan-400 disabled:bg-gray-600 text-gray-900 rounded font-bold text-sm flex items-center justify-center gap-2"
            >
              <LightningIcon /> Protect Selection
            </button>
          </div>
        )}

        {activeTool === 'serialtodate' && (
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-2">📅 Excel Serial → Date Text</label>
            <p className="text-xs text-gray-500 mb-3">
              Converts Excel date serial numbers (like <span className="font-mono text-orange-400">45715</span>) to date text (like <span className="font-mono text-cyan-400">02/27/2025</span>) for use in Intacct updates.
            </p>
            
            <div className="bg-gray-800 border border-gray-600 rounded p-3 mb-3">
              <div className="text-xs text-gray-400 mb-2">When to use:</div>
              <ul className="text-xs text-gray-500 space-y-1">
                <li>• You have date values that show as numbers (e.g., <span className="font-mono">45715</span>)</li>
                <li>• You need to prepare dates for Intacct UPDATE calls</li>
                <li>• Excel is storing dates as serial numbers instead of text</li>
              </ul>
            </div>

            <div className="mb-3">
              <label className="block text-[10px] text-gray-500 mb-1">Output Format</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'MM/DD/YYYY', label: 'MM/DD/YYYY', example: '02/27/2025' },
                  { id: 'DD/MM/YYYY', label: 'DD/MM/YYYY', example: '27/02/2025' },
                  { id: 'YYYY-MM-DD', label: 'YYYY-MM-DD', example: '2025-02-27' },
                  { id: 'MM-DD-YYYY', label: 'MM-DD-YYYY', example: '02-27-2025' },
                ].map(fmt => (
                  <button
                    key={fmt.id}
                    onClick={() => setSerialDateFormat(fmt.id)}
                    className={`px-3 py-2 rounded text-xs font-medium border ${
                      serialDateFormat === fmt.id
                        ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400'
                        : 'bg-gray-600 border-gray-500 text-gray-300 hover:bg-gray-500'
                    }`}
                  >
                    {fmt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="text-xs text-gray-500 mb-3">
              Preview: <span className="font-mono text-orange-400">45715</span> → <span className="text-cyan-400 font-mono">
                {serialDateFormat === 'MM/DD/YYYY' ? '02/27/2025' : 
                 serialDateFormat === 'DD/MM/YYYY' ? '27/02/2025' : 
                 serialDateFormat === 'YYYY-MM-DD' ? '2025-02-27' : '02-27-2025'}
              </span>
            </div>

            <button
              onClick={handleSerialToDate}
              disabled={loading}
              className="w-full py-2 px-4 bg-cyan-500 hover:bg-cyan-400 disabled:bg-gray-600 text-gray-900 rounded font-bold text-sm flex items-center justify-center gap-2"
            >
              <LightningIcon /> Convert Selection
            </button>
          </div>
        )}

        {activeTool === 'fixdate' && (
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-2">🔄 Fix Date Conversion</label>
            <p className="text-xs text-gray-500 mb-3">
              Tools for fixing and converting date formats in your data.
            </p>
            
            {/* Sub-tool selector */}
            <div className="flex flex-wrap gap-1 mb-4">
              {[
                { id: 'reverse', label: 'Reverse Auto-Conv.', icon: '↩️' },
                { id: 'isotimestamp', label: 'ISO Timestamp', icon: '🕐' },
                { id: 'texttodate', label: 'Text → Real Date', icon: '📊' },
                { id: 'striptime', label: 'Strip Time', icon: '✂️' },
              ].map(sub => (
                <button
                  key={sub.id}
                  onClick={() => setDateFixSubTool(sub.id)}
                  className={`px-2 py-1.5 rounded text-[11px] font-medium border transition-colors ${
                    dateFixSubTool === sub.id
                      ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400'
                      : 'bg-gray-600 border-gray-500 text-gray-300 hover:bg-gray-500'
                  }`}
                >
                  <span className="mr-1">{sub.icon}</span>{sub.label}
                </button>
              ))}
            </div>

            {/* Reverse Auto-Conversion (original) */}
            {dateFixSubTool === 'reverse' && (
              <div>
                <p className="text-xs text-gray-500 mb-3">
                  Reverses Excel's automatic date conversion. Use when <span className="font-mono text-orange-400">01-5320</span> became <span className="font-mono text-red-400">1/1/5320</span> or a date serial number.
                </p>
                
                <div className="bg-gray-800 border border-gray-600 rounded p-3 mb-3">
                  <div className="text-xs text-gray-400 mb-2">This tool handles:</div>
                  <ul className="text-xs text-gray-500 space-y-1">
                    <li>• Excel date serial numbers (e.g., <span className="font-mono">1251282</span>)</li>
                    <li>• Date strings like <span className="font-mono">1/1/5320</span></li>
                    <li>• Extracts month and year to reconstruct original format</li>
                  </ul>
                </div>

                <div className="mb-3">
                  <label className="block text-[10px] text-gray-500 mb-1">Output Format</label>
                  <div className="flex gap-2">
                    {[
                      { id: 'MM-YYYY', label: 'MM-YYYY', example: '01-5320' },
                      { id: 'YYYY-MM', label: 'YYYY-MM', example: '5320-01' },
                      { id: 'MM/YYYY', label: 'MM/YYYY', example: '01/5320' },
                    ].map(fmt => (
                      <button
                        key={fmt.id}
                        onClick={() => setDateFormat(fmt.id)}
                        className={`px-3 py-2 rounded text-xs font-medium border ${
                          dateFormat === fmt.id
                            ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400'
                            : 'bg-gray-600 border-gray-500 text-gray-300 hover:bg-gray-500'
                        }`}
                      >
                        {fmt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="text-xs text-gray-500 mb-3">
                  Preview: <span className="font-mono text-red-400">1/1/5320</span> → <span className="text-cyan-400 font-mono">
                    {dateFormat === 'MM-YYYY' ? '01-5320' : dateFormat === 'YYYY-MM' ? '5320-01' : '01/5320'}
                  </span>
                </div>

                <div className="bg-orange-500/10 border border-orange-500/30 rounded p-2 mb-3">
                  <p className="text-xs text-orange-400">
                    ⚠️ <strong>Important:</strong> Only use on cells you KNOW were incorrectly converted. This tool assumes the original format was MM-YYYY (like 01-5320).
                  </p>
                </div>

                <button
                  onClick={handleFixDateConversion}
                  disabled={loading}
                  className="w-full py-2 px-4 bg-cyan-500 hover:bg-cyan-400 disabled:bg-gray-600 text-gray-900 rounded font-bold text-sm flex items-center justify-center gap-2"
                >
                  <LightningIcon /> Fix Selection
                </button>
              </div>
            )}

            {/* ISO Timestamp Parser */}
            {dateFixSubTool === 'isotimestamp' && (
              <div>
                <p className="text-xs text-gray-500 mb-3">
                  Converts ISO timestamps like <span className="font-mono text-orange-400">1979-03-13T00:00:00</span> into clean date text.
                </p>

                <div className="bg-gray-800 border border-gray-600 rounded p-3 mb-3">
                  <div className="text-xs text-gray-400 mb-2">Handles formats like:</div>
                  <ul className="text-xs text-gray-500 space-y-1">
                    <li>• <span className="font-mono">1979-03-13T00:00:00</span></li>
                    <li>• <span className="font-mono">2024-01-15T14:30:00.000Z</span></li>
                    <li>• <span className="font-mono">2024-01-15</span> (date only, no time)</li>
                  </ul>
                </div>

                <div className="mb-3">
                  <label className="block text-[10px] text-gray-500 mb-1">Output Format</label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { id: 'MM/DD/YYYY', label: 'MM/DD/YYYY', example: '03/13/1979' },
                      { id: 'DD/MM/YYYY', label: 'DD/MM/YYYY', example: '13/03/1979' },
                      { id: 'YYYY-MM-DD', label: 'YYYY-MM-DD', example: '1979-03-13' },
                      { id: 'MM-DD-YYYY', label: 'MM-DD-YYYY', example: '03-13-1979' },
                    ].map(fmt => (
                      <button
                        key={fmt.id}
                        onClick={() => setIsoOutputFormat(fmt.id)}
                        className={`px-3 py-2 rounded text-xs font-medium border ${
                          isoOutputFormat === fmt.id
                            ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400'
                            : 'bg-gray-600 border-gray-500 text-gray-300 hover:bg-gray-500'
                        }`}
                      >
                        {fmt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="text-xs text-gray-500 mb-3">
                  Preview: <span className="font-mono text-orange-400">1979-03-13T00:00:00</span> → <span className="text-cyan-400 font-mono">
                    {isoOutputFormat === 'MM/DD/YYYY' ? '03/13/1979' : 
                     isoOutputFormat === 'DD/MM/YYYY' ? '13/03/1979' : 
                     isoOutputFormat === 'YYYY-MM-DD' ? '1979-03-13' : '03-13-1979'}
                  </span>
                </div>

                <button
                  onClick={handleIsoTimestamp}
                  disabled={loading}
                  className="w-full py-2 px-4 bg-cyan-500 hover:bg-cyan-400 disabled:bg-gray-600 text-gray-900 rounded font-bold text-sm flex items-center justify-center gap-2"
                >
                  <LightningIcon /> Convert Selection
                </button>
              </div>
            )}

            {/* Text to Real Excel Date */}
            {dateFixSubTool === 'texttodate' && (
              <div>
                <p className="text-xs text-gray-500 mb-3">
                  Converts text that <em>looks</em> like a date into an actual Excel date so it sorts and calculates correctly.
                </p>

                <div className="bg-gray-800 border border-gray-600 rounded p-3 mb-3">
                  <div className="text-xs text-gray-400 mb-2">When to use:</div>
                  <ul className="text-xs text-gray-500 space-y-1">
                    <li>• Dates sort wrong (e.g., <span className="font-mono">02/01/2006</span> before <span className="font-mono">03/13/1979</span>)</li>
                    <li>• Cells show a date but Excel treats them as text</li>
                    <li>• You used formulas to build a date string and need it recognized</li>
                  </ul>
                </div>

                <div className="bg-gray-800 border border-gray-600 rounded p-3 mb-3">
                  <div className="text-xs text-gray-400 mb-2">Accepts these text formats:</div>
                  <ul className="text-xs text-gray-500 space-y-1">
                    <li>• <span className="font-mono">MM/DD/YYYY</span> or <span className="font-mono">M/D/YYYY</span></li>
                    <li>• <span className="font-mono">MM-DD-YYYY</span> or <span className="font-mono">M-D-YYYY</span></li>
                    <li>• <span className="font-mono">YYYY-MM-DD</span></li>
                  </ul>
                </div>

                <div className="text-xs text-gray-500 mb-3">
                  Preview: <span className="font-mono text-orange-400">03/13/1979</span> (text) → <span className="text-cyan-400 font-mono">03/13/1979</span> (real date — sortable!)
                </div>

                <div className="bg-orange-500/10 border border-orange-500/30 rounded p-2 mb-3">
                  <p className="text-xs text-orange-400">
                    ⚠️ <strong>Note:</strong> Output format is always <span className="font-mono">mm/dd/yyyy</span>. Ambiguous dates (e.g., 01/02/2024) are interpreted as MM/DD/YYYY.
                  </p>
                </div>

                <button
                  onClick={handleTextToRealDate}
                  disabled={loading}
                  className="w-full py-2 px-4 bg-cyan-500 hover:bg-cyan-400 disabled:bg-gray-600 text-gray-900 rounded font-bold text-sm flex items-center justify-center gap-2"
                >
                  <LightningIcon /> Convert Selection
                </button>
              </div>
            )}

            {/* Strip Time */}
            {dateFixSubTool === 'striptime' && (
              <div>
                <p className="text-xs text-gray-500 mb-3">
                  Removes the time portion from datetime strings, keeping just the date.
                </p>

                <div className="bg-gray-800 border border-gray-600 rounded p-3 mb-3">
                  <div className="text-xs text-gray-400 mb-2">Handles formats like:</div>
                  <ul className="text-xs text-gray-500 space-y-1">
                    <li>• <span className="font-mono">2024-01-15T14:30:00</span> → <span className="font-mono text-cyan-400">2024-01-15</span></li>
                    <li>• <span className="font-mono">2024-01-15 14:30:00</span> → <span className="font-mono text-cyan-400">2024-01-15</span></li>
                    <li>• <span className="font-mono">1/15/2024 2:30:00 PM</span> → <span className="font-mono text-cyan-400">1/15/2024</span></li>
                  </ul>
                </div>

                <div className="text-xs text-gray-500 mb-3">
                  Useful when you only need the date for Intacct updates or comparisons.
                </div>

                <button
                  onClick={handleStripTime}
                  disabled={loading}
                  className="w-full py-2 px-4 bg-cyan-500 hover:bg-cyan-400 disabled:bg-gray-600 text-gray-900 rounded font-bold text-sm flex items-center justify-center gap-2"
                >
                  <LightningIcon /> Strip Time from Selection
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {processedCount > 0 && (
        <div className="text-xs text-cyan-400 text-center">
          Last operation: {processedCount} cell(s) transformed
        </div>
      )}
    </div>
  );
};

export default ToolsModePanel;